import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import {
  ON_SIGNUP_ERROR_MESSAGE,
  GIFTING_QUERY_PARAM,
  ALT_CAPTCHA_ERROR_MESSAGE,
  VA_QUERY_PARAM,
  VA_INITIAL_FREE_YEAR_ID,
  SUBSCRIBE_SOURCE_URL_PARAM_NAME,
} from 'Utils/Constants';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { hasEntitlementCookie } from 'Utils/Entitlements';
import {
  getExistingUserRedirects,
  getSheerVerificationId,
  incrementExistingUserRedirects,
  isSheerIdPlan,
} from 'Utils/SheerIdHelpers';
import { isDefaultPlanFlow } from './Analytics/Amplitude';
import { getDefaultConfig, getState } from 'Utils/Store';
import { isSheerProductSKU, getSheerProgramFromSKU } from 'Utils/SheerIdHelpers';
import { isNonAnonymousUser } from './UserCookie';
import { createPaymentAccount } from 'Api/ProfileApi';
import { heapIdentify } from 'Utils/Analytics/Heap';
import { setBrazeUser } from 'Utils/Braze';
import { signIn, getEntitlements } from 'State/User';
import { fetchSubscriptionStatus } from 'State/Subscriptions';
import { setLocation } from './Path';
import { willFetchVeteransAdvAfterSignup } from 'State/Plans';
import { getUserSheerInfo } from 'State/Integrations';
import { store } from 'State/store';

export const createUserAccount = async (values, onSignInSuccess, onSignUpSuccess, analyticTraits) => {
  let recaptchaToken;
  const { signInCaptchaEnabled, signInCaptchaSiteKey } = getDefaultConfig();
  if (signInCaptchaEnabled && window.grecaptcha) {
    recaptchaToken = await window.grecaptcha.execute(signInCaptchaSiteKey, { action: 'submit' });
  }
  const res = await createPaymentAccount(values, recaptchaToken);
  const { accessToken, refreshToken, cpCustomerID, userType } = res;

  const { email } = values;
  const userData = {
    ...res,
    email,
    accessToken,
    refreshToken,
    profileId: cpCustomerID,
  };

  if (accessToken) {
    await onSignInSuccess(userData, true);
    const utagData = {
      profile_type: userType,
      newsletter_opt_in: 'yes',
      dcg_profile_id: cpCustomerID,
    };

    trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_COMPLETED, { ...analyticTraits }, utagData);

    return onSignUpSuccess();
  }

  const { failureMessage } = res;
  const errorCode = (failureMessage && failureMessage.errorCode) || 'server-side';

  let errorMsg = ON_SIGNUP_ERROR_MESSAGE.onVerificationError;
  if (failureMessage?.errorMessage) {
    errorMsg = failureMessage.errorMessage.includes('reCaptcha')
      ? ALT_CAPTCHA_ERROR_MESSAGE
      : failureMessage.errorMessage;
  }

  trackEvent(
    TRACKING_ENUMS.EVENT_SIGNUP_ERROR,
    {
      page_error_type: errorCode,
      page_error_description: errorMsg,
      ...analyticTraits,
    },
    {
      dcg_profile_id: cpCustomerID,
      error_type: errorCode,
      error_message: errorMsg,
    }
  );
  return onSubmitReactFormError(errorMsg, errorCode);
};

function waitForStoreState() {
  function isDataReady(state) {
    const subscription = state.subscription;
    return !subscription.isFetching && !subscription.isFetchingStatus;
  }

  const currentState = getState();
  if (isDataReady(currentState)) {
    return Promise.resolve(currentState);
  }

  return new Promise((resolve) => {
    const unsubscribe = store.subscribe((state) => {
      if (isDataReady(state)) {
        unsubscribe();
        resolve(state);
      }
    });
  });
}

export const signInRedirect = async (entitlementFromSignIn, location) => {
  // Quick fix to ensure data like subscription is fetched and committed to the store before getState is called.
  // This seems to be the first place where the store is accessed for the fetched data so the wait is done here.
  const storeState = await waitForStoreState();
  const {
    appConfig: { defaultConfig, routes },
    purchaseMode: { requirePlanSelection, isGifting = false },
    secondScreen: { isDeviceActivation, code },
    subscription,
    integrations: { bango },
    plans: { selectedPlanId },
  } = storeState;

  let isExternalRedirect, redirectTo;

  const { rootDomain, hideHasSubScreenOnSignUp, alwaysRefreshOnRootSignin } = defaultConfig;
  const { subscriptionId: isBango, productId: bangoProductId, isSiriusXM } = bango;

  let search = isGifting ? GIFTING_QUERY_PARAM : '';
  // set default path
  let path = '/';
  let step = '';
  let state = {};
  let redirectUrl = '';

  // if return_to param is passed to the page
  const query = new URLSearchParams(location.search);
  const returnToParam = query.get('return_to');
  const [returnUrl, returnToQueryString] = (returnToParam || '').split('?');
  const hasEntitlement = hasEntitlementCookie() || entitlementFromSignIn;
  const isLocalhost = window.location.hostname.includes('local');

  if (isGifting) {
    redirectTo = routes.giftingReviewAndRedeem;
  }

  if (isDeviceActivation) {
    if (!hasEntitlement) {
      redirectTo = routes.signupPlans;
    } else {
      redirectTo = `${routes.secondScreenSuccess}/?regCode=${code}`;
      isExternalRedirect = true;
    }
  }

  let isInvalidReturnTo = false;

  if (returnUrl) {
    isExternalRedirect = !!returnUrl.match(/http/i);

    // Remove `return_to` screens that only apply to users without an active subscription
    const screensToRemoveForSubscribedUser = [routes.signupCreateAccount];
    const isSubscribedUserScreen = screensToRemoveForSubscribedUser.some((path) => returnUrl.includes(path));
    isInvalidReturnTo =
      isSubscribedUserScreen &&
      (subscription.hasActiveSubscription || subscription.hasActiveSubscriptionV2) &&
      !returnToQueryString?.includes('sheerVerification=');

    if (!isInvalidReturnTo) {
      redirectTo = returnUrl;
    }
  }

  // if coming through veterans advantage/WeSalute link
  const isVeteransAdv = query.get(VA_QUERY_PARAM) || (returnUrl || '').includes(`?${VA_QUERY_PARAM}=`);
  if (isVeteransAdv) {
    redirectTo = `/account/payment/${VA_INITIAL_FREE_YEAR_ID}`;
    search = location.search;
  }

  // if redirect has specific path
  if (redirectTo) {
    path = redirectTo;
    if (hasEntitlement && !isDeviceActivation && !isGifting && !isExternalRedirect && !isVeteransAdv) {
      path = !isLocalhost ? rootDomain : '/';
      if (hideHasSubScreenOnSignUp || !!path.match(/http/i)) {
        isExternalRedirect = true;
      }
    }
  } else {
    // `from` is sourced from the `LinkProps.To` param from react-router-dom which can either be a partial `Path` object
    // or a string path. since the following code here expects `from` to be an object, we account both possibilites
    // and convert it to a partial `Path` object
    let from = {
      pathname: '/',
      search: '',
    };
    const locationFrom = location?.state?.from;
    if (locationFrom) {
      if (typeof locationFrom === 'string') {
        from = {
          pathname: locationFrom,
          search: '',
        };
      } else {
        from = {
          ...locationFrom,
          pathname: locationFrom.pathname ?? '/',
          search: locationFrom.search ?? '',
        };
      }
    }
    // added signin route as part of condition because now with central auth, user is automatically redirected again,
    // retaining all router params including `from` from the previous page.
    // thus it becomes possible for `from` to be populated with /signin; we want to prevent this, because otherwise
    // the user is redirected back to /signin a second time -- ultimately harmless but an unnecessary extra redirect.
    const isRootUrl = from.pathname === '/' || from.pathname === routes.signin;
    if (selectedPlanId && !subscription.hasActiveSubscription && !subscription.hasActiveSubscriptionV2) {
      path = routes.payment;
    } else if (isRootUrl && !hasEntitlement && requirePlanSelection) {
      path = routes.signupPlans;
    } else if (isRootUrl && document.referrer !== '') {
      path = !isLocalhost ? rootDomain : '/';
      isExternalRedirect = true;
    } else {
      path = from.pathname;
      search = from.search;
      state = from.state;
    }
  }

  // check if alwaysRefreshOnRootSignin for d2c products only
  if (path === '/' && alwaysRefreshOnRootSignin) {
    isExternalRedirect = true;
  }
  if (isDeviceActivation && isExternalRedirect) {
    setLocation(path);
    return {
      isExternalRedirect,
    };
  }

  if (hasEntitlement && returnUrl && returnUrl.includes(routes.signupCreateAccount)) {
    if (subscription.isIAPUser && subscription.hasActiveSubscription && getSheerVerificationId()) {
      path = routes.signupCurrentSubscriberMilitaryFirst;
      isExternalRedirect = false;
    } else if (!isInvalidReturnTo) {
      path = returnUrl;
      isExternalRedirect = !!returnUrl.match(/http/i);
    }
  }

  if (
    isDefaultPlanFlow() &&
    !subscription.hasActiveSubscription &&
    !subscription.hasActiveSubscriptionV2 &&
    !returnUrl
  ) {
    path = routes.payment;
  }

  if (isBango) {
    path = isSiriusXM ? routes.siriusXMBango : routes.verizonBango;
    if (isSheerProductSKU(bangoProductId)) {
      const sheerProgramType = getSheerProgramFromSKU(bangoProductId);
      if (sheerProgramType) {
        path = routes.sheerIdVerification;
        search = `sheerVerification=${sheerProgramType}`;
      } else {
        path = routes.sheerIdVerification;
      }
    }
  }

  //For when an non-sheerid existing user is in the middle of the sheerid verification process
  const existingUserThreeTimes =
    subscription.hasActiveSubscription &&
    !isSheerIdPlan(subscription.activeSubscriptionId) &&
    !(subscription.purchaseType === 'verizonBango') &&
    !(subscription.purchaseType === 'siriusXMBango') &&
    getExistingUserRedirects() < 3;
  // prioritize veterans adv route if query param is provided
  if (
    (await isNonAnonymousUser()) &&
    (subscription.isNewUser || subscription.isChurnedUser || existingUserThreeTimes) &&
    !isVeteransAdv
  ) {
    try {
      const response = await getUserSheerInfo();
      if (response?.verificationId) {
        path = routes.sheerIdVerification;
        if (response?.program) {
          path = `${routes.sheerIdVerification}`;
          search = `?sheerVerification=${response.program}`;
        }
        step = response?.currentStep;
        redirectUrl = response?.bangoRedirectUrl ?? '';
        if (existingUserThreeTimes) incrementExistingUserRedirects();
      }
    } catch (error) {
      if (error?.verificationId) {
        path = routes.sheerIdVerification;
        if (error?.program) {
          path = `${routes.sheerIdVerification}`;
          search = `?sheerVerification=${error.program}`;
        }
        step = error?.currentStep;
        redirectUrl = error?.bangoRedirectUrl ?? '';
        if (existingUserThreeTimes) incrementExistingUserRedirects();
      }
    }
  }

  /*
    This happens when create account page has the sheerVerification param in url after a logged out user
    that hasn't started verification is redirected there from sheer verification page. This will redirect
    them back to verification page to start verification.
  */
  if (path.includes('?sheerVerification=') && !path.includes(routes.sheerIdVerification)) {
    search = `?sheerVerification=${path.split('sheerVerification=')[1]}`;
    path = `${routes.sheerIdVerification}`;
  }

  // Only add the search params relative to the `return_to` param if the final `path` is the
  // one from `return_to`
  if (returnToParam && path === returnUrl) {
    search = new URLSearchParams(returnToQueryString).toString();
  }

  if (isExternalRedirect) {
    setLocation(path);
    return {
      isExternalRedirect,
    };
  } else {
    return {
      redirectPath: path,
      redirectSearch: search,
      redirectState: state,
      redirectStep: step,
      redirectUrl,
    };
  }
};

export const handleSignInSuccess = () => {
  return async (userData, isInSignUp) => {
    const { profileId } = userData;
    heapIdentify(profileId);

    const { hasEntitlements: hasEntitlementsV1, hasEntitlementsV2 } = await getEntitlements(1);
    await fetchSubscriptionStatus();
    /**
     * please make sure we call getEntitlements before signIn since we need to check the entitlement
     * in renderRedirect SignInContainer.js; if we call signIn before getEntitlements,
     * it triggers component re-render and entitlementCookie is not set
     */
    signIn(userData);
    setBrazeUser(profileId);
    if (!isInSignUp) trackEvent(TRACKING_ENUMS.EVENT_LOGIN_COMPLETE);

    return hasEntitlementsV1 || hasEntitlementsV2;
  };
};

export const handleSignUpSuccess = (isSheerIdVerification, isVeteransAdv, urlParams, navigate) => {
  return () => {
    const { appConfig, integrations, plans, purchaseMode } = getState();
    const { isGifting } = purchaseMode;
    const { routes } = appConfig;
    const { subscriptionId: isBango, productId: bangoProductId, isSiriusXM } = integrations.bango;
    const { isPlanSelected, selectedPlan } = plans;

    let path = routes.payment;

    if (isVeteransAdv) {
      const veteransAdvId = urlParams.get(VA_QUERY_PARAM);
      path = `${routes.payment}/${VA_INITIAL_FREE_YEAR_ID}?${VA_QUERY_PARAM}=${veteransAdvId}`;
      // temporarily set flag during this session so that PaymentPage doesn't immediately
      //    redirect the user back to plan selection before the VA plan is fetched & selected
      willFetchVeteransAdvAfterSignup(true);
    } else if (isBango) {
      path = isSiriusXM ? routes.siriusXMBango : routes.verizonBango;
      if (isSheerProductSKU(bangoProductId)) {
        const sheerProgramType = getSheerProgramFromSKU(bangoProductId);
        path = sheerProgramType
          ? `${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`
          : routes.sheerIdVerification;
      }
    } else if (isSheerIdVerification) {
      const sheerProgramType = urlParams.get('sheerVerification');
      path = sheerProgramType
        ? `${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`
        : routes.sheerIdVerification;
    } else if (isGifting) {
      path = routes.giftingReviewAndRedeem;
    } else if (isPlanSelected && selectedPlan && selectedPlan.hasMerchandise) {
      path = routes.shipping;
    } else if (!isPlanSelected) {
      path = routes.signupPlans;
    }

    if (urlParams.has(SUBSCRIBE_SOURCE_URL_PARAM_NAME)) {
      const subscribeSource = urlParams.get(SUBSCRIBE_SOURCE_URL_PARAM_NAME);
      const [pathname, queryString = ''] = path.split('?');
      const searchParams = new URLSearchParams(queryString);

      searchParams.set(SUBSCRIBE_SOURCE_URL_PARAM_NAME, subscribeSource);
      path = `${pathname}?${searchParams.toString()}`;
    }

    navigate(path);
  };
};
